/**
 * Dashboard 状态管理 Store
 * 管理仪表盘页面的聚合数据和统计信息
 */

import Decimal from 'decimal.js';

import { create } from 'zustand';
import {
  getCreditTransactions,
  getCreditsAppRanking,
  getCreditsServiceSummary,
  getDashboardOverview,
} from '@/api';
import type { CreditTransaction, DashboardOverview } from '@/api';
import { tryM } from '@/utils/money';

/**
 * App 用量统计（来自 GET /credits/app-ranking 单条 item）
 */
export interface AppUsageStats {
  appId: string;
  appName: string;
  totalCalls: number;
  totalCredits: number;
  totalUsdtMicro: number;
  totalUsdt: string;
}

/**
 * 服务用量统计（来自 GET /credits/service-summary 单条 item）
 */
export interface ServiceUsageStats {
  serviceName: string;
  totalCalls: number;
  totalCredits: number;
  totalUsdt: string;
  /** 近 30 天相对再前 30 天的积分消耗环比（百分比字符串，如 `+12.5%` / `—`） */
  trend: string;
}

/**
 * 充值类资金流水简项（由 GET /credits/transactions 中 type=recharge 映射）
 */
export interface RechargeHistoryItem {
  id: string;
  date: string;
  amountUsdt: string;
  description: string;
  status: string;
}

export interface DashboardState {
  appUsageStats: AppUsageStats[];
  serviceUsageStats: ServiceUsageStats[];
  rechargeHistory: RechargeHistoryItem[];
  overview: DashboardOverview | null;
  isLoadingUsageStats: boolean;
  isLoadingRechargeHistory: boolean;
  isLoadingOverview: boolean;
  lastUpdated: number | null;

  setAppUsageStats: (stats: AppUsageStats[]) => void;
  setServiceUsageStats: (stats: ServiceUsageStats[]) => void;
  setRechargeHistory: (history: RechargeHistoryItem[]) => void;
  setOverview: (overview: DashboardOverview | null) => void;
  setUsageStatsLoading: (loading: boolean) => void;
  setRechargeHistoryLoading: (loading: boolean) => void;
  clear: () => void;
  refreshUsageStats: () => Promise<void>;
  refreshRechargeHistory: (limit?: number) => Promise<void>;
  refreshOverview: () => Promise<void>;
  refreshAll: () => Promise<void>;
}

const initialState = {
  appUsageStats: [],
  serviceUsageStats: [],
  rechargeHistory: [],
  overview: null,
  isLoadingUsageStats: false,
  isLoadingRechargeHistory: false,
  isLoadingOverview: false,
  lastUpdated: null,
};

/** 近 30 天 vs 前 30 天积分消耗环比（用积分维度与接口 `prev30TotalCredits` 对齐） */
function formatServiceCreditsTrend(currentCredits: number, prevCredits: number): string {
  const c = tryM(currentCredits);
  const p = tryM(prevCredits);
  if (c === null || p === null) return '—';
  if (p.lte(0)) return c.gt(0) ? '+100%' : '—';
  // Decimal-safe percentage: ((c - p) / p) * 100, rounded to 1 decimal.
  const rounded = c.minus(p).div(p).mul(100).toDecimalPlaces(
    1,
    Decimal.ROUND_HALF_UP,
  );
  if (rounded.isZero()) return '0%';
  const sign = rounded.isPositive() ? '+' : '';
  return `${sign}${rounded.toString()}%`;
}

function transformCreditRechargeHistory(
  transactions: CreditTransaction[] | undefined
): RechargeHistoryItem[] {
  if (!transactions?.length) return [];

  return transactions
    .filter((tx) => tx.type === 'recharge')
    .map((tx) => {
      const date = new Date(tx.createdAt * 1000);
      const dateStr = date.toISOString().split('T')[0] ?? '';
      const amt = tx.amount;
      const amountLabel = amt > 0 ? `+${amt}` : String(amt);
      return {
        id: String(tx.id),
        date: dateStr,
        /** 字段名历史遗留：此处存积分变动数量（非 USDT） */
        amountUsdt: amountLabel,
        description: tx.description,
        status: 'Completed',
      };
    })
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

export const useDashboardStore = create<DashboardState>()((set, get) => ({
  ...initialState,

      setAppUsageStats: (stats) => {
        set({
          appUsageStats: stats,
          lastUpdated: Date.now(),
        });
      },

      setServiceUsageStats: (stats) => {
        set({
          serviceUsageStats: stats,
          lastUpdated: Date.now(),
        });
      },

      setRechargeHistory: (history) => {
        set({
          rechargeHistory: history,
          lastUpdated: Date.now(),
        });
      },

      setOverview: (overview) => {
        set({
          overview,
          lastUpdated: Date.now(),
        });
      },

      setUsageStatsLoading: (loading) => {
        set({ isLoadingUsageStats: loading });
      },

      setRechargeHistoryLoading: (loading) => {
        set({ isLoadingRechargeHistory: loading });
      },

      clear: () => {
        set(initialState);
      },

      refreshUsageStats: async () => {
        set({ isLoadingUsageStats: true });
        try {
          const [rankingRes, summaryRes] = await Promise.all([
            getCreditsAppRanking(),
            getCreditsServiceSummary(),
          ]);

          if (rankingRes.ok) {
            const appStats: AppUsageStats[] = rankingRes.data.items.map((item) => ({
              appId: item.appId,
              appName: item.appName,
              totalCalls: item.totalCalls,
              totalCredits: item.totalCredits,
              totalUsdtMicro: item.totalUsdtMicro,
              totalUsdt: item.totalUsdt,
            }));
            set({ appUsageStats: appStats });
          }

          if (summaryRes.ok) {
            const serviceStats: ServiceUsageStats[] = summaryRes.data.items.map(
              (item) => ({
                serviceName: item.serviceName,
                totalCalls: item.totalCalls,
                totalCredits: item.totalCredits,
                totalUsdt: item.totalUsdt,
                trend: formatServiceCreditsTrend(
                  item.totalCredits,
                  item.prev30TotalCredits
                ),
              })
            );
            set({ serviceUsageStats: serviceStats });
          }

          if (rankingRes.ok || summaryRes.ok) {
            set({ lastUpdated: Date.now() });
          }
        } catch (error) {
          console.error('[Dashboard Store] Failed to refresh usage stats:', error);
        } finally {
          set({ isLoadingUsageStats: false });
        }
      },

      refreshRechargeHistory: async (limit = 10) => {
        set({ isLoadingRechargeHistory: true });
        try {
          const result = await getCreditTransactions({
            page: 1,
            limit,
            type: 'recharge',
          });
          if (result.ok) {
            const txs = result.data.transactions.filter((tx) => tx.type === 'recharge');
            const history = transformCreditRechargeHistory(txs);
            set({
              rechargeHistory: history,
              lastUpdated: Date.now(),
            });
          }
        } catch (error) {
          console.error('[Dashboard Store] Failed to refresh recharge history:', error);
        } finally {
          set({ isLoadingRechargeHistory: false });
        }
      },

      refreshOverview: async () => {
        set({ isLoadingOverview: true });
        try {
          const result = await getDashboardOverview();
          if (result.ok) {
            set({
              overview: result.data,
              lastUpdated: Date.now(),
            });
          }
        } catch (error) {
          console.error('[Dashboard Store] Failed to refresh overview:', error);
        } finally {
          set({ isLoadingOverview: false });
        }
      },

      refreshAll: async () => {
        const state = get();
        await Promise.all([
          state.refreshUsageStats(),
          state.refreshRechargeHistory(10),
          state.refreshOverview(),
        ]);
      },
}));

export const useAppUsageStats = () =>
  useDashboardStore((state) => state.appUsageStats);

export const useServiceUsageStats = () =>
  useDashboardStore((state) => state.serviceUsageStats);

export const useRechargeHistory = () =>
  useDashboardStore((state) => state.rechargeHistory);

export const useUsageStatsLoading = () =>
  useDashboardStore((state) => state.isLoadingUsageStats);

export const useRechargeHistoryLoading = () =>
  useDashboardStore((state) => state.isLoadingRechargeHistory);

export const useDashboardOverview = () =>
  useDashboardStore((state) => state.overview);

export const useUsdtBalance = () =>
  useDashboardStore((state) => {
    // Decimal-safe parse: handles oversized API strings without precision loss.
    // The number return is for display / chart use only; precision-critical
    // arithmetic should consume the string selector below instead.
    const d = tryM(state.overview?.balance?.availableUsdt);
    return d === null ? 0 : d.toNumber();
  });

export const useUsdtLocked = () =>
  useDashboardStore((state) => {
    const d = tryM(state.overview?.balance?.lockedUsdt);
    return d === null ? 0 : d.toNumber();
  });

/**
 * Precision-preserving USDT balance selector. Returns the raw API decimal
 * string (e.g. "10.500000"), or "0" when the field is missing / unparseable.
 *
 * Use this for any arithmetic / comparison; the `number`-returning
 * {@link useUsdtBalance} is only safe for display and chart axes.
 */
export const useUsdtBalanceString = () =>
  useDashboardStore((state) => {
    const d = tryM(state.overview?.balance?.availableUsdt);
    return d === null ? '0' : d.toFixed();
  });

/** Precision-preserving locked USDT selector; companion to {@link useUsdtBalanceString}. */
export const useUsdtLockedString = () =>
  useDashboardStore((state) => {
    const d = tryM(state.overview?.balance?.lockedUsdt);
    return d === null ? '0' : d.toFixed();
  });

export const useOverviewLoading = () =>
  useDashboardStore((state) => state.isLoadingOverview);

export const useRefreshOverview = () =>
  useDashboardStore((state) => state.refreshOverview);
